import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { Project, ProjectFile } from '../types';

interface QueryTableFullScreenProps {
  project: Project;
  onBack: () => void;
}

const QueryTableFullScreen: React.FC<QueryTableFullScreenProps> = ({ project, onBack }) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const [structuredQueries, setStructuredQueries] = useState<any[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<any | null>(null);
  const [queryTableData, setQueryTableData] = useState<ProjectFile[]>([]);
  const [queryResults, setQueryResults] = useState<Map<string, any>>(new Map());
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newQueryText, setNewQueryText] = useState('');
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New loading state
  const [expandedResponse, setExpandedResponse] = useState<{
    text: string;
    fileName: string;
    columnName: string;
  } | null>(null);

  const loadQueryTable = useCallback(async (queryId: number) => {
    setIsLoading(true); // Start loading
    try {
      const queryData = await apiService.getStructuredQuery(queryId);
      setSelectedQuery(queryData);
      setQueryTableData(queryData.files || []);
      
      // Clear previous results first to avoid stale data
      setQueryResults(new Map());
      
      // Convert results map to our format with proper refresh
      const resultsMap = new Map();
      Object.entries(queryData.results || {}).forEach(([key, result]: [string, any]) => {
        // Ensure we have a proper result object structure
        if (result && typeof result === 'object') {
          resultsMap.set(key, {
            status: result.status || 'completed',
            result_text: result.result_text || result.response || '',
            error_message: result.error_message || null,
            created_at: result.created_at || new Date().toISOString()
          });
        }
      });
      setQueryResults(resultsMap);

    } catch (error) {
      console.error('Error loading query table:', error);
      notificationService.error('Failed to load query table');
    } finally {
      setIsLoading(false); // End loading
    }
  }, []);

  const loadStructuredQueries = useCallback(async () => {
    setIsLoading(true); // Start loading
    try {
      const response = await apiService.getStructuredQueries(project.id);
      setStructuredQueries(response.queries || []);
      
      // If there's a query, load the first one by default
      if (response.queries && response.queries.length > 0) {
        await loadQueryTable(response.queries[0].id);
      }

    } catch (error) {
      console.error('Error loading structured queries:', error);
      notificationService.error('Failed to load structured queries');
    } finally {
      setIsLoading(false); // End loading
    }
  }, [project.id, setStructuredQueries, loadQueryTable, setIsLoading]);

  useEffect(() => {
    loadStructuredQueries();
  }, [project.id, loadStructuredQueries]);

  const createInitialQuery = async () => {
    setShowCreateQueryModal(true);
  };

  const handleCreateQuery = async (queryData: any) => {
    setIsLoading(true); // Start loading
    try {
      // Create structured query with the original query as the first column
      const response = await apiService.createStructuredQuery(project.id, {
        ...queryData,
        columns: [
          {
            name: 'Original Query',
            query: queryData.original_query,
            type: 'analysis'
          }
        ]
      });
      
      notificationService.success('Structured query created successfully!');
      setShowCreateQueryModal(false);
      
      // Reload structured queries and load the new one
      await loadStructuredQueries();
      
      // Auto-run the original query for all files
      if (response.id) {
        setTimeout(async () => {
          try {
            await apiService.runStructuredQuery(response.id);
            notificationService.success('Running original query for all documents...');
          } catch (error) {
            console.error('Error auto-running query:', error);
          } finally {
            // setIsLoading(false); // End loading for auto-run - removed as per task
          }
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error creating structured query:', error);
      notificationService.error('Failed to create structured query');
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleAddColumn = async () => {
    if (!newQueryText.trim() || !selectedQuery) {
      notificationService.warning('Please enter a query');
      return;
    }

    if (selectedQuery.columns.length >= 10) {
      notificationService.warning('Maximum of 10 query columns allowed');
      return;
    }
 
     setIsAddingColumn(true);
     setIsLoading(true); // Start loading
 
     try {
       const response = await apiService.addQueryColumn(selectedQuery.id, {
         name: newQueryText.trim(),
         query: newQueryText.trim(),
         type: 'analysis'
       });
 
       setShowAddColumnModal(false);
       setNewQueryText('');
 
       const shortQuery = newQueryText.length > 30 ? newQueryText.substring(0, 27) + '...' : newQueryText;
       notificationService.success(`Added new query column: "${shortQuery}"`);
 
       // Reload the query table
       await loadQueryTable(selectedQuery.id);
 
       // Auto-run the new column for all files with proper format
       if (response.id && queryTableData.length > 0) {
         setTimeout(async () => {
           try {
              // Set all cells in this new column to pending state first
              const updatedResults = new Map(queryResults);
              queryTableData.forEach(file => {
                const resultKey = `${file.id}-${response.id}`;
                updatedResults.set(resultKey, {
                  status: 'pending',
                  result_text: '',
                  error_message: null
                });
              });
              setQueryResults(updatedResults);
 
              notificationService.success(`🔄 Running new column for all ${queryTableData.length} files...`);
 
              // Run the new column for all files with proper format (using true for simple format)
              const promises = queryTableData.map(async (file, index) => {
                try {
                  // Add a small delay to stagger requests
                  await new Promise(resolve => setTimeout(resolve, index * 100));
                  
                  const cellResponse = await apiService.runQueryCell(selectedQuery.id, response.id, file.id, true);
                  
                  // Update individual cell result in real-time
                  setQueryResults(prev => {
                    const newResults = new Map(prev);
                    newResults.set(`${file.id}-${response.id}`, cellResponse);
                    return newResults;
                  });
                  
                  return { success: true, fileId: file.id };
                } catch (error: any) {
                  console.error(`Error running new column for file ${file.id}:`, error);
                  
                  // Update individual cell with error
                  setQueryResults(prev => {
                    const newResults = new Map(prev);
                    newResults.set(`${file.id}-${response.id}`, {
                      status: 'failed',
                      result_text: '',
                      error_message: error.response?.data?.detail || error.message
                    });
                    return newResults;
                  });
                  
                  return { success: false, fileId: file.id, error };
                }
              });
 
              // Wait for all queries to complete
              const results = await Promise.allSettled(promises);
              
              const successful = results.filter(result =>
                result.status === 'fulfilled' && result.value.success
              ).length;
              
              const failed = results.length - successful;
              
              if (failed === 0) {
                notificationService.success(`✅ Completed new column for all ${queryTableData.length} files`);
              } else if (successful > 0) {
                notificationService.warning(`⚠️ Completed ${successful} files, ${failed} failed`);
              } else {
                notificationService.error(`❌ All queries failed for new column`);
              }
              
            } catch (error) {
              console.error('Error auto-running new column:', error);
              notificationService.error('Failed to run new column for all files');
            } finally {
              // setIsLoading(false); // End loading for auto-run - removed as per task
            }
          }, 500);
       }
 
     } catch (error) {
       console.error('Error adding query column:', error);
       notificationService.error('Failed to add query column');
     } finally {
       setIsAddingColumn(false);
       setIsLoading(false); // End loading
     }
   };

  const runQueryForFile = async (fileIndex: number, columnIndex: number) => {
    if (!selectedQuery) return;
    
    const file = queryTableData[fileIndex];
    const column = selectedQuery.columns[columnIndex];
    const resultKey = `${file.id}-${column.id}`;

    if (!file || !column) return;

    // Set loading state for individual cell
    setQueryResults(prev => new Map(prev.set(resultKey, {
      status: 'pending',
      result_text: '',
      error_message: null
    })));

    try {
      // Use z for direct paragraph responses
      const response = await apiService.runQueryCell(selectedQuery.id, column.id, file.id, true);
      setQueryResults(prev => new Map(prev.set(resultKey, response)));

    } catch (error: any) {
      console.error('Error running query for file:', error);
      setQueryResults(prev => new Map(prev.set(resultKey, {
        status: 'failed',
        result_text: '',
        error_message: error.response?.data?.detail || error.message
      })));
    } finally {
      // setIsLoading(false); // End main loading - removed as per task
    }
  };

  const runQueryForAllFiles = async (columnIndex: number) => {
    if (!selectedQuery) return;
    
    const column = selectedQuery.columns[columnIndex];
    if (!column) return;

    // Set all cells in this column to pending state
    const updatedResults = new Map(queryResults);
    queryTableData.forEach(file => {
      const resultKey = `${file.id}-${column.id}`;
      updatedResults.set(resultKey, {
        status: 'pending',
        result_text: '',
        error_message: null
      });
    });
    setQueryResults(updatedResults);

    notificationService.success(`🔄 Running query for all ${queryTableData.length} files...`);

    try {
      // Run queries for each file individually to show progress
      const promises = queryTableData.map(async (file, index) => {
        try {
          // Add a small delay to stagger requests and show progression
          await new Promise(resolve => setTimeout(resolve, index * 100));
          
          const response = await apiService.runQueryCell(selectedQuery.id, column.id, file.id, true);
          
          // Update individual cell result
          setQueryResults(prev => {
            const newResults = new Map(prev);
            newResults.set(`${file.id}-${column.id}`, response);
            return newResults;
          });
          
          return { success: true, fileId: file.id };
        } catch (error: any) {
          console.error(`Error running query for file ${file.id}:`, error);
          
          // Update individual cell with error
          setQueryResults(prev => {
            const newResults = new Map(prev);
            newResults.set(`${file.id}-${column.id}`, {
              status: 'failed',
              result_text: '',
              error_message: error.response?.data?.detail || error.message
            });
            return newResults;
          });
          
          return { success: false, fileId: file.id, error };
        }
      });

      // Wait for all queries to complete
      const results = await Promise.allSettled(promises);
      
      const successful = results.filter(result =>
        result.status === 'fulfilled' && result.value.success
      ).length;
      
      const failed = results.length - successful;
      
      if (failed === 0) {
        notificationService.success(`✅ Completed query for all ${queryTableData.length} files`);
      } else if (successful > 0) {
        notificationService.warning(`⚠️ Completed ${successful} files, ${failed} failed`);
      } else {
        notificationService.error(`❌ All queries failed`);
      }

    } catch (error: any) {
      console.error('Error running query for all files:', error);
      notificationService.error('Failed to run query for all files');
    } finally {
      // setIsLoading(false); // End main loading - removed as per task
    }
  };

  const getFileIcon = (fileType: string) => {
    switch(fileType?.toLowerCase()) {
      case 'pdf': return 'pdf';
      case 'docx': return 'word';
      case 'pptx': return 'ppt';
      case 'xlsx': return 'excel';
      default: return 'text';
    }
  };

  const renderQueryCell = (fileIndex: number, columnIndex: number) => {
    if (!selectedQuery) return null;
    
    const file = queryTableData[fileIndex];
    const column = selectedQuery.columns[columnIndex];
    const resultKey = `${file.id}-${column.id}`;
    const result = queryResults.get(resultKey);

    if (!result) {
      return (
        <div 
          onClick={() => runQueryForFile(fileIndex, columnIndex)}
          style={{
            textAlign: 'center',
            padding: '0.75rem',
            cursor: 'pointer',
            border: '1px dashed #d1d5db',
            borderRadius: '0.375rem',
            minHeight: '60px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--primary-color)';
            e.currentTarget.style.background = 'var(--bg-secondary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-color)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <i className="bi bi-play-circle" style={{ fontSize: '1.25rem', color: 'var(--primary-color)' }}></i>
          <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Click to run</small>
        </div>
      );
    }

    if (result.status === 'pending') {
      return (
        <div style={{
          textAlign: 'center',
          padding: '0.75rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.375rem',
          minHeight: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div className="spinner-border spinner-border-sm" style={{ color: 'var(--primary-color)' }}></div>
          <small style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>Running...</small>
        </div>
      );
    }

    if (result.status === 'failed') {
      return (
        <div style={{
          padding: '0.75rem',
          background: 'var(--danger-bg)',
          border: '1px solid var(--danger-border)',
          borderRadius: '0.375rem',
          minHeight: '60px'
        }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--danger-color)' }}>
            <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.25rem' }}></i>
            Query failed
          </div>
          <small style={{ color: 'var(--text-secondary)' }}>{result.error_message}</small>
        </div>
      );
    }

    // Completed result
    const shortResponse = result.result_text && result.result_text.length > 100 
      ? result.result_text.substring(0, 97) + '...' 
      : result.result_text || '';

    return (
      <div
        style={{
          padding: '0.75rem',
          background: 'var(--success-bg)',
          border: '1px solid var(--success-border)',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          minHeight: '60px',
          transition: 'all 0.2s ease'
        }}
        onClick={() => {
          setExpandedResponse({
            text: result.result_text,
            fileName: file.display_name,
            columnName: column.name
          });
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {shortResponse}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '0.5rem'
        }}>
          <small style={{ color: 'var(--success-color)' }}>
            <i className="bi bi-check-circle" style={{ marginRight: '0.25rem' }}></i>
            Completed
          </small>
          <small style={{ color: 'var(--text-secondary)' }}>
            <i className="bi bi-eye"></i>
          </small>
        </div>
      </div>
    );
  };

  // Show empty state if no structured queries exist
  if (structuredQueries.length === 0) {
    return (
      <div style={{
        height: '100vh',
        background: 'var(--bg-color)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          background: 'var(--bg-color)',
          borderBottom: '1px solid var(--border-color)',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>
              {project.name}
            </h3>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              {project.file_count} documents • {(project.total_size_bytes / (1024 * 1024)).toFixed(2)} MB
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '4rem 2rem'
        }}>
          <div>
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <i className="bi bi-table" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}></i>
            </div>
            <h4 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem 0', fontWeight: 600 }}>
              No Query Tables Yet
            </h4>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>
              Create a structured query table to analyze multiple documents with column-based queries.
              <br />
              Perfect for contract reviews, due diligence, and comparative analysis.
            </p>
            <button
              onClick={createInitialQuery}
              style={{
                background: 'var(--text-primary)',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem',
                color: 'var(--bg-color)',
                fontWeight: 500,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <i className="bi bi-plus" style={{ marginRight: '0.5rem' }}></i>
              Create Query Table
            </button>
          </div>
        </div>

        {/* Create Query Modal */}
        {showCreateQueryModal && (
          <CreateQueryModal 
            onSubmit={handleCreateQuery} 
            onCancel={() => setShowCreateQueryModal(false)} 
          />
        )}
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      background: 'var(--bg-color)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative' // Added for loader positioning
    }}>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1080, // Higher than other modals
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: 'white',
          fontSize: '1.2rem'
        }}>
          <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: 'var(--primary-color)' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading Query Table...</p>
        </div>
      )}
      {/* Header */}
      <div style={{
        background: 'var(--bg-color)',
        borderBottom: '1px solid var(--border-color)',
        padding: '1rem 2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>
              {selectedQuery?.name || 'Query Table'}
            </h3>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem', 
              marginTop: '0.25rem' 
            }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                {queryTableData.length} documents
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                {selectedQuery?.columns?.length || 0}/50 columns remaining
              </span>
              {selectedQuery?.last_run_at && (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Answered {new Date(selectedQuery.last_run_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {/* Query Table Selector */}
          {structuredQueries.length > 1 && (
            <select
              value={selectedQuery?.id || ''}
              onChange={(e) => {
                const queryId = parseInt(e.target.value);
                if (queryId) {
                  loadQueryTable(queryId);
                }
              }}
              style={{
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.5rem 0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                outline: 'none',
                minWidth: '200px'
              }}
            >
              {structuredQueries.map((query) => (
                <option key={query.id} value={query.id}>
                  {query.name}
                </option>
              ))}
            </select>
          )}
          
          <button
            onClick={createInitialQuery}
            style={{
              background: 'var(--bg-color)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className="bi bi-plus"></i>
            New Query Table
          </button>
          <button
            style={{
              background: 'var(--text-primary)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              color: 'var(--bg-color)',
              fontWeight: 500,
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Export
          </button>
        </div>
      </div>

      {/* Query Description */}
      {selectedQuery && (
        <div style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.5rem',
          padding: '1rem',
          margin: '1rem 2rem 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <div style={{
              background: 'var(--primary-bg)',
              borderRadius: '0.25rem',
              padding: '0.5rem',
              minWidth: '32px',
              textAlign: 'center'
            }}>
              <i className="bi bi-lightbulb" style={{ color: 'var(--primary-color)', fontSize: '1rem' }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '0.25rem' }}>
                Original query
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                {selectedQuery.original_query}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        margin: '1rem 2rem 0',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={() => setShowAddColumnModal(true)}
            style={{
              background: 'var(--text-primary)',
              border: 'none',
              borderRadius: '0.375rem',
              padding: '0.5rem 0.75rem',
              color: 'var(--bg-color)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500
            }}
          >
            <i className="bi bi-plus"></i>
            Add Column
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={() => selectedQuery && loadQueryTable(selectedQuery.id)}
            style={{
              background: 'var(--bg-color)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.375rem',
              padding: '0.5rem 0.75rem',
              color: 'var(--text-secondary)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500
            }}
            title="Refresh table data"
          >
            <i className="bi bi-arrow-clockwise"></i>
            Refresh
          </button>
          <small style={{ color: 'var(--text-secondary)' }}>
            {queryResults.size} results loaded
          </small>
        </div>
      </div>

      {/* Table Container */}
      <div style={{
        flex: 1,
        margin: '1rem 2rem 2rem',
        background: 'var(--bg-color)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `300px repeat(${selectedQuery?.columns?.length || 0}, 250px)`,
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)',
          minWidth: 'fit-content'
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            borderRight: '1px solid var(--border-color)',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            position: 'sticky',
            left: 0,
            background: 'var(--bg-secondary)',
            zIndex: 10
          }}>
            <i className="bi bi-sort-alpha-down" style={{ fontSize: '0.9rem' }}></i>
            Name
          </div>
          {selectedQuery?.columns?.map((column: any, index: number) => (
            <div 
              key={column.id}
              style={{
                padding: '0.75rem 1rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                borderRight: index < selectedQuery.columns.length - 1 ? '1px solid var(--border-color)' : 'none',
                fontSize: '0.85rem'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 600,
                    marginBottom: '0.25rem'
                  }}>
                    {column.name.length > 30 ? column.name.substring(0, 27) + '...' : column.name}
                  </div>
                  <small style={{ color: 'var(--text-secondary)' }}>{column.type}</small>
                </div>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                  onClick={() => runQueryForAllFiles(index)}
                  title="Run for all files"
                >
                  <i className="bi bi-play"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Table Body */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'auto'
        }}>
          {queryTableData.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem 0',
              color: 'var(--text-secondary)'
            }}>
              <i className="bi bi-inbox" style={{ fontSize: '2.5rem', color: 'var(--border-color)' }}></i>
              <p style={{ marginTop: '1rem' }}>No completed documents in this project</p>
            </div>
          ) : (
            queryTableData.map((file, fileIndex) => (
              <div 
                key={file.id}
                style={{ 
                  display: 'grid',
                  gridTemplateColumns: `300px repeat(${selectedQuery?.columns?.length || 0}, 250px)`,
                  borderBottom: fileIndex < queryTableData.length - 1 ? '1px solid var(--border-color)' : 'none',
                  transition: 'background 0.2s ease',
                  minWidth: 'fit-content'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-color)';
                }}
              >
                {/* File Name Cell */}
                <div style={{
                  padding: '1rem',
                  borderRight: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'sticky',
                  left: 0,
                  background: 'inherit',
                  zIndex: 5
                }}>
                  <i
                    className={`bi bi-file-earmark-${getFileIcon(file.file_type)}`}
                    style={{
                      fontSize: '1.25rem',
                      color: 'var(--primary-color)',
                      marginRight: '0.75rem'
                    }}
                  ></i>
                  <div>
                    <div style={{
                      color: 'var(--text-primary)',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      lineHeight: 1.3,
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {file.display_name}
                    </div>
                    <small style={{ color: 'var(--text-secondary)' }}>
                      {(file.file_size / (1024 * 1024)).toFixed(1)} MB
                    </small>
                  </div>
                </div>

                {/* Query Result Cells */}
                {selectedQuery?.columns?.map((column: any, columnIndex: number) => (
                  <div 
                    key={`${fileIndex}-${columnIndex}`}
                    style={{
                      padding: '0.75rem',
                      borderRight: columnIndex < selectedQuery.columns.length - 1 ? '1px solid var(--border-color)' : 'none'
                    }}
                  >
                    {renderQueryCell(fileIndex, columnIndex)}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Column Modal */}
      {showAddColumnModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1060,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
           style={{
             background: 'var(--bg-color)',
             border: '1px solid var(--border-color)',
             borderRadius: '0.75rem',
             width: '90%',
             maxWidth: '500px',
             padding: '1.5rem'
           }}
         >
           <div style={{
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             marginBottom: '1rem'
           }}>
             <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>
               <i className="bi bi-plus-circle" style={{ marginRight: '0.5rem' }}></i>
               Add Query Column
             </h5>
             <button
               onClick={() => setShowAddColumnModal(false)}
               style={{
                 background: 'none',
                 border: 'none',
                 color: 'var(--text-secondary)',
                 fontSize: '1.2rem',
                 cursor: 'pointer'
               }}
             >
               <i className="bi bi-x"></i>
             </button>
           </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
                Column Query
              </label>
              <textarea
                value={newQueryText}
                onChange={(e) => setNewQueryText(e.target.value)}
                placeholder="Enter your query (e.g., 'What are the key risks mentioned?')"
                rows={3}
                style={{
                  width: '100%',
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  padding: '0.75rem',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                  outline: 'none'
              }}
              />
              <small style={{ color: 'var(--text-secondary)' }}>
                This query will be run against all documents in the project
              </small>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddColumnModal(false)}
                style={{
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddColumn}
                disabled={isAddingColumn || !newQueryText.trim()}
                style={{
                  background: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  color: 'var(--bg-color)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  opacity: (!newQueryText.trim()) ? 0.6 : 1
                }}
              >
                {isAddingColumn ? (
                  <>
                    <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.5rem' }}></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus" style={{ marginRight: '0.5rem' }}></i>
                    Add Column
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Query Modal */}
      {showCreateQueryModal && (
        <CreateQueryModal 
          onSubmit={handleCreateQuery} 
          onCancel={() => setShowCreateQueryModal(false)} 
        />
      )}

      {/* Expanded Response Modal */}
      {expandedResponse && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 1070,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={() => setExpandedResponse(null)}
        >
          <div
           style={{
             background: 'var(--bg-color)',
             border: '1px solid var(--border-color)',
             borderRadius: '0.75rem',
             width: '100%',
             maxWidth: '800px',
             maxHeight: '80vh',
             display: 'flex',
             flexDirection: 'column',
             overflow: 'hidden'
           }}
           onClick={(e) => e.stopPropagation()}
         >
           {/* Modal Header */}
           <div style={{
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'flex-start',
             padding: '1.5rem 1.5rem 1rem',
             borderBottom: '1px solid var(--border-color)'
           }}>
             <div style={{ flex: 1, marginRight: '1rem' }}>
               <h5 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600, fontSize: '1.1rem' }}>
                 <i className="bi bi-file-earmark-text" style={{ marginRight: '0.5rem', color: 'var(--primary-color)' }}></i>
                 {expandedResponse.fileName}
               </h5>
               <div style={{
                 color: 'var(--text-secondary)',
                 fontSize: '0.9rem',
                 background: 'var(--bg-secondary)',
                 padding: '0.5rem 0.75rem',
                 borderRadius: '0.375rem',
                 border: '1px solid var(--border-color)'
               }}>
                 <i className="bi bi-question-circle" style={{ marginRight: '0.5rem' }}></i>
                 <strong>Query:</strong> {expandedResponse.columnName}
               </div>
             </div>
              <button
                onClick={() => setExpandedResponse(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ 
              flex: 1,
              padding: '1.5rem',
              overflowY: 'auto',
              lineHeight: 1.6
            }}>
              <div style={{
                color: 'var(--text-primary)',
                fontSize: '1rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {expandedResponse.text}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(expandedResponse.text);
                  notificationService.success('Response copied to clipboard');
                }}
                style={{
                  background: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  color: 'var(--bg-color)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <i className="bi bi-clipboard"></i>
                Copy
              </button>
              <button
                onClick={() => {
                  console.log('Closing modal and navigating to chat...');
                  setExpandedResponse(null); // Close the modal
                  // Use a timeout to ensure the state update for closing the modal has a chance to process
                  // before navigation, which might cause a re-render and flicker or prevent closing.
                  setTimeout(() => {
                    navigate(`/chat/${project.id}`);
                  }, 0);
                }}
                style={{
                  background: 'var(--primary-color)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <i className="bi bi-chat-dots"></i>
                Chat with AI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Query Modal Component
interface CreateQueryModalProps {
  onSubmit: (queryData: any) => void;
  onCancel: () => void;
}

const CreateQueryModal: React.FC<CreateQueryModalProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    original_query: '',
    query_type: 'analysis'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.original_query.trim()) {
      notificationService.warning('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        original_query: formData.original_query.trim(),
        query_type: formData.query_type,
        columns: []
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.75rem',
          width: '90%',
          maxWidth: '600px',
          padding: '2rem'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>
            <i className="bi bi-table" style={{ marginRight: '0.75rem' }}></i>
            Create Query Table
          </h4>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
              Query Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Contract Review Analysis"
              required
              style={{
                width: '100%',
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
              Original Query *
            </label>
            <textarea
              name="original_query"
              value={formData.original_query}
              onChange={handleInputChange}
              placeholder="e.g., Identify the parties, change of control provisions, termination, and red flags generally"
              rows={3}
              required
              style={{
                width: '100%',
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                resize: 'vertical',
                outline: 'none'
              }}
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              This will be the main query that gets broken down into column sub-queries
            </small>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Optional description of what this query table will analyze"
              rows={2}
              style={{
                width: '100%',
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                resize: 'vertical',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim() || !formData.original_query.trim()}
              style={{
                background: 'var(--text-primary)',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem',
                color: 'var(--bg-color)',
                cursor: 'pointer',
                fontWeight: 500,
                opacity: (!formData.name.trim() || !formData.original_query.trim()) ? 0.6 : 1
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.5rem' }}></div>
                  Creating...
                </>
              ) : (
                <>
                  <i className="bi bi-plus" style={{ marginRight: '0.5rem' }}></i>
                  Create Query Table
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QueryTableFullScreen;
